import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { Checkbox } from '../../../components/ui/Checkbox';
import { rolesService } from '../../../services/supabaseService';

const RoleManagementSection = () => {
  const defaultRoles = [
    {
      id: 1,
      name: 'Manager',
      description: 'Department managers with team oversight',
      permissions: {
        viewEmployees: true,
        editEmployees: true,
        deleteEmployees: false,
        viewViolations: true,
        addViolations: true,
        editViolations: true,
        deleteViolations: false,
        viewReports: true,
        exportData: true,
        manageSettings: false,
        approveViolations: true
      },
      employeeCount: 8
    },
    {
      id: 2,
      name: 'HR Admin',
      description: 'Human resources administrators',
      permissions: {
        viewEmployees: true,
        editEmployees: true,
        deleteEmployees: true,
        viewViolations: true,
        addViolations: true,
        editViolations: true,
        deleteViolations: true,
        viewReports: true,
        exportData: true,
        manageSettings: true,
        approveViolations: true
      },
      employeeCount: 3
    },
    {
      id: 3,
      name: 'Team Lead',
      description: 'Team leaders with limited oversight',
      permissions: {
        viewEmployees: true,
        editEmployees: false,
        deleteEmployees: false,
        viewViolations: true,
        addViolations: true,
        editViolations: false,
        deleteViolations: false,
        viewReports: true,
        exportData: false,
        manageSettings: false,
        approveViolations: false
      },
      employeeCount: 12
    },
    {
      id: 4,
      name: 'Employee',
      description: 'Standard employee access',
      permissions: {
        viewEmployees: false,
        editEmployees: false,
        deleteEmployees: false,
        viewViolations: false,
        addViolations: false,
        editViolations: false,
        deleteViolations: false,
        viewReports: false,
        exportData: false,
        manageSettings: false,
        approveViolations: false
      },
      employeeCount: 45
    }
  ];

  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [isAdding, setIsAdding] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    permissions: {
      viewEmployees: false,
      editEmployees: false,
      deleteEmployees: false,
      viewViolations: false,
      addViolations: false,
      editViolations: false,
      deleteViolations: false,
      viewReports: false,
      exportData: false,
      manageSettings: false,
      approveViolations: false
    }
  });

  // Load roles from Supabase on component mount
  useEffect(() => {
    loadRoles();
  }, []);

  const loadRoles = async () => {
    try {
      setLoading(true);
      const data = await rolesService?.getAll();
      setRoles(data);
    } catch (error) {
      console.error('Error loading roles:', error);
    } finally {
      setLoading(false);
    }
  };

  const [permissionGroups] = useState([
    {
      title: 'Employee Management',
      permissions: [
        { key: 'viewEmployees', label: 'View Employees', description: 'View employee list and profiles' },
        { key: 'editEmployees', label: 'Edit Employees', description: 'Modify employee information' },
        { key: 'deleteEmployees', label: 'Delete Employees', description: 'Remove employees from system' }
      ]
    },
    {
      title: 'Violation Management',
      permissions: [
        { key: 'viewViolations', label: 'View Violations', description: 'View violation records' },
        { key: 'addViolations', label: 'Add Violations', description: 'Create new violation records' },
        { key: 'editViolations', label: 'Edit Violations', description: 'Modify existing violations' },
        { key: 'deleteViolations', label: 'Delete Violations', description: 'Remove violation records' },
        { key: 'approveViolations', label: 'Approve Violations', description: 'Approve or reject violations' }
      ]
    },
    {
      title: 'Reports & Data',
      permissions: [
        { key: 'viewReports', label: 'View Reports', description: 'Access analytics and reports' },
        { key: 'exportData', label: 'Export Data', description: 'Export data to external formats' }
      ]
    },
    {
      title: 'System Administration',
      permissions: [
        { key: 'manageSettings', label: 'Manage Settings', description: 'Configure system settings' }
      ]
    }
  ]);

  const handleAdd = () => {
    setIsAdding(true);
    setEditingId(null);
    setFormData({
      name: '',
      description: '',
      permissions: {
        viewEmployees: false,
        editEmployees: false,
        deleteEmployees: false,
        viewViolations: false,
        addViolations: false,
        editViolations: false,
        deleteViolations: false,
        viewReports: false,
        exportData: false,
        manageSettings: false,
        approveViolations: false
      }
    });
  };

  const handleEdit = (role) => {
    setEditingId(role?.id);
    setIsAdding(false);
    setFormData({
      name: role?.name,
      description: role?.description,
      permissions: { ...role?.permissions }
    });
  };

  const handleSave = async () => {
    try {
      if (isAdding) {
        const newRole = await rolesService?.create({
          ...formData,
          employeeCount: 0
        });
        if (newRole) {
          setRoles(prev => [...prev, newRole]);
          setIsAdding(false);
        }
      } else {
        const updatedRole = await rolesService?.update(editingId, formData);
        if (updatedRole) {
          setRoles(prev => prev?.map(r => r?.id === editingId ? updatedRole : r));
          setEditingId(null);
        }
      }
      setFormData({
        name: '',
        description: '',
        permissions: {
          viewEmployees: false,
          editEmployees: false,
          deleteEmployees: false,
          viewViolations: false,
          addViolations: false,
          editViolations: false,
          deleteViolations: false,
          viewReports: false,
          exportData: false,
          manageSettings: false,
          approveViolations: false
        }
      });
    } catch (error) {
      console.error('Error saving role:', error);
    }
  };

  const handleCancel = () => {
    setEditingId(null);
    setIsAdding(false);
    setFormData({
      name: '',
      description: '',
      permissions: {
        viewEmployees: false,
        editEmployees: false,
        deleteEmployees: false,
        viewViolations: false,
        addViolations: false,
        editViolations: false,
        deleteViolations: false,
        viewReports: false,
        exportData: false,
        manageSettings: false,
        approveViolations: false
      }
    });
  };

  const handleDelete = (id) => {
    setDeletingId(id);
  };

  const confirmDelete = async () => {
    try {
      const success = await rolesService?.delete(deletingId);
      if (success) {
        setRoles(prev => prev?.filter(r => r?.id !== deletingId));
      }
      setDeletingId(null);
    } catch (error) {
      console.error('Error deleting role:', error);
      setDeletingId(null);
    }
  };

  const cancelDelete = () => {
    setDeletingId(null);
  };

  const handlePermissionChange = (key, value) => {
    setFormData(prev => ({
      ...prev,
      permissions: {
        ...prev?.permissions,
        [key]: value
      }
    }));
  };

  const countActivePermissions = (permissions) => {
    return Object.values(permissions)?.filter(Boolean)?.length;
  };

  const renderRoleForm = () => (
    <div className="space-y-6">
      <div className="space-y-4">
        <Input
          label="Role Name"
          value={formData?.name}
          onChange={(e) => setFormData({ ...formData, name: e?.target?.value })}
          placeholder="e.g., Department Manager"
        />
        <div>
          <label className="block text-sm font-medium text-foreground mb-1.5">
            Description
          </label>
          <textarea
            value={formData?.description}
            onChange={(e) => setFormData({ ...formData, description: e?.target?.value })}
            placeholder="Brief description of this role"
            rows={2}
            className="w-full px-4 py-2 border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary resize-none"
          />
        </div>
      </div>

      <div>
        <h4 className="font-heading font-semibold text-base text-foreground mb-4">
          Permissions
        </h4>
        <div className="space-y-6">
          {permissionGroups?.map((group) => (
            <div key={group?.title} className="bg-muted/30 rounded-lg p-4">
              <h5 className="font-heading font-semibold text-sm text-foreground mb-3 flex items-center gap-2">
                <Icon name="public/assets/images/Real_logo-1771587686730.png" size={16} className="text-primary" />
                {group?.title}
              </h5>
              <div className="space-y-3">
                {group?.permissions?.map((perm) => (
                  <Checkbox
                    key={perm?.key}
                    label={perm?.label}
                    description={perm?.description}
                    checked={formData?.permissions?.[perm?.key]}
                    onChange={(e) => handlePermissionChange(perm?.key, e?.target?.checked)}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="flex justify-end gap-2 pt-4 border-t border-border">
        <Button variant="outline" size="sm" onClick={handleCancel}>
          Cancel
        </Button>
        <Button 
          variant="default" 
          size="sm" 
          onClick={handleSave}
          disabled={!formData?.name?.trim()}
        >
          {isAdding ? 'Add Role' : 'Save Changes'}
        </Button>
      </div>
    </div>
  );

  return (
    <div className="p-6">
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-sm text-muted-foreground">Loading roles...</p>
          </div>
        </div>
      ) : (
        <>
          <div className="mb-6 flex items-start justify-between">
            <div>
              <h2 className="font-heading font-semibold text-xl text-foreground mb-1">
                Role Management
              </h2>
              <p className="font-body text-sm text-muted-foreground">
                Define roles and assign permissions to control access levels across the system
              </p>
            </div>
            <Button
              variant="default"
              size="sm"
              iconName="Plus"
              onClick={handleAdd}
              disabled={isAdding || editingId !== null}
            >
              Add Role
            </Button>
          </div>

          <div className="bg-primary/5 border border-primary/20 rounded-lg p-4 mb-6">
            <div className="flex items-start gap-3">
              <Icon name="Info" size={20} className="text-primary mt-0.5" />
              <div>
                <h4 className="font-heading font-semibold text-sm text-foreground mb-1">
                  Role-Based Access Control
                </h4>
                <p className="text-sm text-muted-foreground">
                  Roles determine what actions users can perform in the system. Assign permissions carefully to maintain security and compliance.
                </p>
              </div>
            </div>
          </div>

          <div className="space-y-4">
            {isAdding && (
              <div className="bg-card border-2 border-primary rounded-lg p-6">
                <div className="mb-4">
                  <h3 className="font-heading font-semibold text-lg text-foreground mb-2">
                    Add New Role
                  </h3>
                </div>
                {renderRoleForm()}
              </div>
            )}

            {roles?.map((role) => (
              <div
                key={role?.id}
                className="bg-card border border-border rounded-lg p-4"
              >
                {editingId === role?.id ? (
                  <div>
                    <div className="mb-4">
                      <h3 className="font-heading font-semibold text-lg text-foreground mb-2">
                        Edit Role
                      </h3>
                    </div>
                    {renderRoleForm()}
                  </div>
                ) : (
                  <div>
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-center gap-3">
                        <div className="flex items-center justify-center w-12 h-12 rounded-lg bg-primary/10">
                          <Icon name="public/assets/images/Real_logo-1771587686730.png" size={24} className="text-primary" />
                        </div>
                        <div>
                          <h4 className="font-heading font-semibold text-lg text-foreground">
                            {role?.name}
                          </h4>
                          <p className="text-sm text-muted-foreground">
                            {role?.description}
                          </p>
                          <div className="flex items-center gap-3 mt-1">
                            <span className="text-xs text-muted-foreground">
                              {role?.employeeCount} {role?.employeeCount === 1 ? 'employee' : 'employees'}
                            </span>
                            <span className="text-xs text-muted-foreground">
                              •
                            </span>
                            <span className="text-xs text-primary font-semibold">
                              {countActivePermissions(role?.permissions)} permissions active
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          iconName="Edit2"
                          onClick={() => handleEdit(role)}
                          disabled={isAdding || editingId !== null}
                        />
                        <Button
                          variant="ghost"
                          size="sm"
                          iconName="Trash2"
                          onClick={() => handleDelete(role?.id)}
                          disabled={isAdding || editingId !== null}
                          className="text-destructive hover:text-destructive hover:bg-destructive/10"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
                      {permissionGroups?.map((group) => (
                        <div key={group?.title} className="bg-muted/30 rounded-lg p-3">
                          <div className="flex items-center gap-2 mb-2">
                            <Icon name="CheckCircle2" size={14} className="text-success" />
                            <span className="text-xs font-semibold text-muted-foreground">
                              {group?.title}
                            </span>
                          </div>
                          <div className="space-y-1">
                            {group?.permissions?.map((perm) => (
                              <div key={perm?.key} className="flex items-center gap-1.5">
                                {role?.permissions?.[perm?.key] ? (
                                  <Icon name="Check" size={12} className="text-success" />
                                ) : (
                                  <Icon name="X" size={12} className="text-muted-foreground/40" />
                                )}
                                <span className={`text-xs ${
                                  role?.permissions?.[perm?.key] 
                                    ? 'text-foreground' 
                                    : 'text-muted-foreground/60'
                                }`}>
                                  {perm?.label}
                                </span>
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Delete Confirmation Dialog */}
          {deletingId && (
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
              <div className="bg-card rounded-lg p-6 max-w-md w-full mx-4 elevation-lg">
                <div className="flex items-start gap-3 mb-4">
                  <div className="flex items-center justify-center w-12 h-12 rounded-full bg-destructive/10">
                    <Icon name="AlertTriangle" size={24} className="text-destructive" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-heading font-semibold text-lg text-foreground mb-1">
                      Delete Role
                    </h3>
                    <p className="text-sm text-muted-foreground mb-2">
                      Are you sure you want to delete <span className="font-semibold text-foreground">{roles?.find(r => r?.id === deletingId)?.name}</span>?
                    </p>
                    <p className="text-xs text-warning">
                      Warning: {roles?.find(r => r?.id === deletingId)?.employeeCount} employees are currently assigned this role. They will need to be reassigned.
                    </p>
                  </div>
                </div>
                <div className="flex justify-end gap-2">
                  <Button variant="outline" size="sm" onClick={cancelDelete}>
                    Cancel
                  </Button>
                  <Button variant="destructive" size="sm" onClick={confirmDelete}>
                    Delete Role
                  </Button>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default RoleManagementSection;